import { createUpdateInstruction } from '@metaplex-foundation/mpl-token-metadata';
import { PublicKey, SYSVAR_INSTRUCTIONS_PUBKEY } from '@solana/web3.js';
import isEqual from 'lodash.isequal';
import { parseTokenMetadataAuthorization } from '../Authorization.mjs';
import { NoInstructionsToSendError } from '../../../errors/SdkError.mjs';
import { useOperation } from '../../../types/Operation.mjs';
import { TransactionBuilder } from '../../../utils/TransactionBuilder.mjs';
import { isNonFungible } from '../models/Metadata.mjs';

const TOKEN_AUTH_RULES_ID = new PublicKey('auth9SigNpDKz4sJJ1DfCTuZrZNSAgh9sFD3rboVmgg');

// -----------------
// Operation
// -----------------

const Key = 'UpdateNftOperation';

/**
 * Updates an existing NFT or SFT.
 *
 * ```ts
 * await metaplex
 *   .nfts()
 *   .update({ nftOrSft, name: "My new NFT name" });
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const updateNftOperation = useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const updateNftOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    const builder = updateNftBuilder(metaplex, operation.input, scope);
    if (builder.isEmpty()) {
      throw new NoInstructionsToSendError(Key);
    }
    return builder.sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Updates an existing NFT or SFT.
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .nfts()
 *   .builders()
 *   .update({ nftOrSft, name: "My new NFT name" });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const updateNftBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    nftOrSft,
    updateAuthority = metaplex.identity(),
    authority = updateAuthority,
    authorizationDetails
  } = params;

  // Programs.
  const tokenMetadataProgram = metaplex.programs().getTokenMetadata(programs);
  const systemProgram = metaplex.programs().getSystem(programs);
  const updateInstructionDataWithoutChanges = toInstructionData(nftOrSft);
  const updateInstructionData = toInstructionData(nftOrSft, params);
  const shouldSendUpdateInstruction = !isEqual(updateInstructionData, updateInstructionDataWithoutChanges);
  const isRemovingVerifiedCollection = !!nftOrSft.collection && !!nftOrSft.collection.verified && params.collection === null;
  const isOverridingVerifiedCollection = !!nftOrSft.collection && !!nftOrSft.collection.verified && !!params.collection && !params.collection.equals(nftOrSft.collection.address);
  const shouldUnverifyCurrentCollection = isRemovingVerifiedCollection || isOverridingVerifiedCollection;

  // Auth.
  const auth = parseTokenMetadataAuthorization(metaplex, {
    mint: nftOrSft.address,
    authority: '__kind' in authority ? authority : {
      __kind: 'metadata',
      updateAuthority: authority
    },
    authorizationDetails,
    programs
  });
  const creatorsInput = params.creators ?? nftOrSft.creators;
  const verifyAdditionalCreatorInstructions = creatorsInput.filter(creator => {
    const currentCreator = nftOrSft.creators.find(({
      address
    }) => address.equals(creator.address));
    const currentlyVerified = currentCreator?.verified ?? false;
    return !!creator.authority && !currentlyVerified;
  }).map(creator => {
    return metaplex.nfts().builders().verifyCreator({
      mintAddress: nftOrSft.address,
      creator: creator.authority
    }, {
      payer,
      programs
    });
  });
  return TransactionBuilder.make().setFeePayer(payer)

  // Unverify current collection before overriding it.
  // Otherwise, the previous collection size will not be properly decremented.
  .when(shouldUnverifyCurrentCollection, builder => builder.add(metaplex.nfts().builders().unverifyCollection({
    mintAddress: nftOrSft.address,
    collectionMintAddress: nftOrSft.collection?.address,
    collectionAuthority: params.oldCollectionAuthority ?? payer,
    isSizedCollection: params.oldCollectionIsSized ?? true
  }, {
    programs,
    payer
  })))

  // Update the metadata account.
  .when(shouldSendUpdateInstruction, builder => builder.add({
    instruction: createUpdateInstruction({
      authority: auth.accounts.authority,
      delegateRecord: auth.accounts.delegateRecord,
      token: auth.accounts.token,
      mint: nftOrSft.address,
      metadata: metaplex.nfts().pdas().metadata({
        mint: nftOrSft.address,
        programs
      }),
      edition: isNonFungible(nftOrSft) ? metaplex.nfts().pdas().masterEdition({
        mint: nftOrSft.address,
        programs
      }) : undefined,
      // tokenRecord, // TODO
      payer: payer.publicKey,
      systemProgram: systemProgram.address,
      sysvarInstructions: SYSVAR_INSTRUCTIONS_PUBKEY,
      authorizationRules: auth.accounts.authorizationRules,
      authorizationRulesProgram: TOKEN_AUTH_RULES_ID
    }, {
      updateArgs: {
        ...updateInstructionData,
        ...auth.data
      }
    }, tokenMetadataProgram.address),
    signers: [payer, ...auth.signers],
    key: params.updateMetadataInstructionKey ?? 'updateMetadata'
  }))

  // Verify additional creators.
  .add(...verifyAdditionalCreatorInstructions)

  // Verify collection.
  .when(!!params.collection && !!params.collectionAuthority, builder => builder.add(metaplex.nfts().builders().verifyCollection({
    mintAddress: nftOrSft.address,
    collectionMintAddress: params.collection,
    collectionAuthority: params.collectionAuthority,
    isDelegated: params.collectionAuthorityIsDelegated ?? false,
    isSizedCollection: params.collectionIsSized ?? true
  }, {
    programs,
    payer
  })));
};
const toInstructionData = (nftOrSft, input = {}) => {
  const creators = input.creators === undefined ? nftOrSft.creators : input.creators.map(creator => {
    const currentCreator = nftOrSft.creators.find(({
      address
    }) => address.equals(creator.address));
    return {
      ...creator,
      verified: currentCreator?.verified ?? false
    };
  });
  return {
    __kind: 'V1',
    newUpdateAuthority: input.newUpdateAuthority ?? null,
    data: {
      name: input.name ?? nftOrSft.name,
      symbol: input.symbol ?? nftOrSft.symbol,
      uri: input.uri ?? nftOrSft.uri,
      sellerFeeBasisPoints: input.sellerFeeBasisPoints ?? nftOrSft.sellerFeeBasisPoints,
      creators: creators.length > 0 ? creators : null
    },
    primarySaleHappened: input.primarySaleHappened ?? null,
    isMutable: input.isMutable ?? null,
    collection: input.collection ? {
      __kind: 'Set',
      fields: [{
        key: input.collection,
        verified: false
      }]
    } : {
      __kind: input.collection === undefined ? 'None' : 'Clear'
    },
    collectionDetails: input.collectionDetails ? {
      __kind: 'Set',
      fields: [input.collectionDetails]
    } : {
      __kind: 'None'
    },
    uses: input.uses ? {
      __kind: 'Set',
      fields: [input.uses]
    } : {
      __kind: input.uses === undefined ? 'None' : 'Clear'
    },
    ruleSet: input.ruleSet ? {
      __kind: 'Set',
      fields: [input.ruleSet]
    } : {
      __kind: input.ruleSet === undefined ? 'None' : 'Clear'
    }
  };
};

export { updateNftBuilder, updateNftOperation, updateNftOperationHandler };
//# sourceMappingURL=updateNft.mjs.map
